import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import cs from "classnames";
import { trackEvent, EVENT_CATEGORY, publicConfig } from "@lululemon/ecom-core";
import { useGoogleMapLoader } from "@lululemon/dsp-utils";
import Head from "next/head";
import {
  Breadcrumbs,
  Image,
  CTABlock,
  Carousel,
  LinkGenerator
} from "@lululemon/dsp-ecom-pattern-library";
import {
  CtaGrid,
  IconListModal,
  useWindowDimensions,
  readQuery
} from "@lululemon/dsp-legacy-shared";
import { graphql } from "@lululemon/dsp-shared";
import { sentryLog } from "@lululemon/dsp-shared/src/logger";
import { convertToDashed, useRegionalize } from "@lululemon/mwa-i18n";

import gql from "graphql-tag"; // eslint-disable-line import/no-named-as-default
import { print } from "graphql/language/printer";

import getAmenitiesIconTextList from "@lululemon/dsp-store-details/src/util/get-amenities-icon-text-list";
import content from "./content/content";
import { STORE_SPACE_DETAILS_EXPERIENTIAL } from "./content/analytics-tags";
import { spaceMeta } from "./content/generateMetaData";

import styles from "./store-space.module.scss";

const storeQuery = print(gql`
  query ($slug: String!) {
    storeBySlug(slug: $slug) {
      id
      name
      slug
      storeNumber
      storeType
      storeStatus
      classesUrl
      eventsUrl
      fullAddress
      city
      state
      country
      tuneUrl
      spaces {
        images {
          id
          accessibilityText
          displayOrder
          tags
          url
        }
        id
        about
        aboutHtml
        amenities
        bookingEmail
        displayOrder
        fuelMenuPdfKeywords
        fuelMenuPdfUrl
        fuelMenuUrl
        hours {
          name
          openHour
          closeHour
        }
        longDescription
        name
        shortDescription
      }
    }
  }
`);

export const StoreSpace = ({ storeData = {}, spaceData = {}, meta = {} }) => {
  const {
    components: {
      breadcrumbs,
      storeSpace: {
        ctaBlocks: ctaBlocksContent,
        ctaGrid: ctaGridContent,
        tuneBedBookingHeading
      }
    }
  } = content;
  // HYDRATION MISMATCH CAUSE
  let isMobileView = false;
  if (process.browser) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    isMobileView = useWindowDimensions().width < 992;
  }

  const [slideIndex, setSlideIndex] = useState(0);

  // required for store locator to load google maps
  const isGoogleMapLoaded = useGoogleMapLoader();

  const carouselImages = spaceData.images
    ?.filter(
      (img) =>
        img.accessibilityText.toLowerCase() !== "store carousel" &&
        img.tags.find((tag) => tag.toLowerCase() === "carousel")
    )
    .map((item) => (
      <span key={`store-space-image-${item.id}`} className={styles.imgWrapper}>
        <Image
          className={styles.img}
          maxWidth={699}
          lazy={false}
          src={item.url}
          alt={item.accessibilityText}
        />
      </span>
    ));

  const getSlidesPerPage = () => {
    const mobileSlidesPerPage = carouselImages?.length === 1 ? 1 : 1.33;
    return isMobileView ? mobileSlidesPerPage : 2.2555;
  };

  const aboutOrEmbeddedAbout = spaceData.aboutHtml ? (
    <p
      className={styles.about}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: spaceData.aboutHtml }}
    />
  ) : (
    <p className={styles.about}>{spaceData.about}</p>
  );

  const heroImage = spaceData.images?.find(
    (img) => img.accessibilityText.toLowerCase() === "hero image"
  );

  const mappedAmenities = getAmenitiesIconTextList(
    spaceData.amenities,
    storeData?.hasOneLinkTranslation
  );

  const directionsLink = `${publicConfig.googleMapsSearchDomain}${storeData.fullAddress}`;

  useEffect(() => {
    trackEvent(EVENT_CATEGORY.PAGE_VIEW, {
      headerUnified:
        `${storeData.country}-${storeData.city}-${storeData.name}-${spaceData.name}`
          .replace(/\W/g, "-")
          .toLowerCase(),
      screenType: "stores",
      gender: "all"
    });
  }, []);

  const spaceBlockContent = (spaceData?.name === "Meditation Space" &&
    storeData?.tuneUrl && {
      heading: tuneBedBookingHeading,
      href: storeData.tuneUrl
    }) || {
    heading: ctaBlocksContent.directions,
    href: directionsLink
  };

  const ctaBlocksWithTitle = (
    <div>
      <CTABlock
        href={storeData.classesUrl}
        heading={ctaBlocksContent.classes}
        data-component-link={STORE_SPACE_DETAILS_EXPERIENTIAL}
        target="_blank"
      />
      <CTABlock
        href={storeData.eventsUrl}
        heading={ctaBlocksContent.events}
        data-component-link={STORE_SPACE_DETAILS_EXPERIENTIAL}
        target="_blank"
      />
      <CTABlock
        href={spaceBlockContent.href}
        heading={spaceBlockContent.heading}
        data-component-link={STORE_SPACE_DETAILS_EXPERIENTIAL}
        target="_blank"
      />
    </div>
  );

  const { regionalizeLink } = useRegionalize();
  const canonicalLink = `${publicConfig.nav.apiHost}${meta.canonicalPath}`;

  const regionalizedCanonical = regionalizeLink(canonicalLink);

  if (!isGoogleMapLoaded) {
    return null;
  }

  return (
    <>
      <Head>
        <title>{meta.title}</title>
        <link rel="canonical" href={regionalizedCanonical} />
        <meta name="description" content={meta.description} />
        <meta name="robots" content={meta.robots} />
        {meta.hrefLangEntries?.map((link) => (
          <link
            key={link.hreflang}
            rel={link.rel}
            href={link.href}
            hrefLang={link.hreflang}
          />
        ))}
      </Head>
      <main className={cs("lll-grid", styles.storeContainer)}>
        <section className={cs("OneLinkNoTx", styles.breadcrumbsContainer)}>
          <Breadcrumbs
            seoItems={[
              { url: "/stores", label: breadcrumbs.storesLabel },
              { url: storeData.slug, label: storeData.name }
            ]}
            // eslint-disable-next-line no-unused-vars
            renderSEOToHead={(seoContent) => {}}
            ariaLabel="Breadcrumb"
          >
            <LinkGenerator href="/stores">
              {breadcrumbs.storesLabel}
            </LinkGenerator>
            <LinkGenerator href={storeData.slug}>
              {storeData.name}
            </LinkGenerator>
          </Breadcrumbs>
        </section>
        <h1 className={styles.spaceName}>{spaceData.name}</h1>
        <div className={styles.hoursAboutContainer}>{aboutOrEmbeddedAbout}</div>
        <div className={styles.ctaBlockList}>{ctaBlocksWithTitle}</div>
        <div className={styles.hero}>
          {heroImage && (
            <Image
              lazy={false}
              className={styles.image}
              src={heroImage.url}
              maxWidth={1005}
            />
          )}
        </div>
        <section className={styles.ctaGridContainer}>
          <CtaGrid
            rightPanelExtraColumn
            heading={ctaGridContent.heading}
            description={spaceData.longDescription}
            leftPanel={
              <IconListModal
                analyticsId={STORE_SPACE_DETAILS_EXPERIENTIAL}
                listItems={mappedAmenities}
                modalSubHeading={storeData.name}
                modalHeading={ctaGridContent.modalHeading}
                openModalText={ctaGridContent.openModalText}
              />
            }
            rightPanel={
              carouselImages && (
                <Carousel
                  slidesPerPage={getSlidesPerPage()}
                  showArrows={carouselImages?.length > 2}
                  showPagination
                  paginationLocation="bottom"
                  slideIndex={slideIndex}
                  onSlideIndexChange={(value) => setSlideIndex(value)}
                  classes={{
                    carousel: styles.carousel,
                    leftArrow: styles.carouselArrow,
                    rightArrow: styles.carouselArrow
                  }}
                >
                  {carouselImages}
                </Carousel>
              )
            }
          />
        </section>
      </main>
    </>
  );
};

StoreSpace.getInitialProps = async ({ query, req, res, nextLocale }) => {
  const { urlQuery } = readQuery({ query, req });
  let { locale } = readQuery({ query, req });

  const slug = `/stores/${urlQuery.country.toLowerCase()}/${urlQuery.city.toLowerCase()}/${urlQuery.storeName.toLowerCase()}`;

  let storeData = {};
  let spaceData = null;
  let is404 = false;

  locale = convertToDashed(nextLocale);

  const hasOneLinkTranslation =
    locale === "fr-CA" ||
    query?.locale === "fr_CA" ||
    query?.locale === "fr-CA" ||
    null;

  let storeError;
  try {
    const response = await graphql({
      query: storeQuery,
      variables: {
        slug
      },
      req,
      res,
      getSecret: req?.app?.locals?.getSecret
    });

    storeData = {
      ...response.data.storeBySlug,
      hasOneLinkTranslation
    };
    spaceData = storeData.spaces.find(
      (space) =>
        space.name.toLowerCase() ===
        urlQuery.spaceName.replace(/-/g, " ").toLowerCase() // fuel-space > fuel space
    );

    if (!spaceData || spaceData === null) {
      throw new Error("No matching space found.");
    }
  } catch (error) {
    storeError = error;
    is404 = true;
    sentryLog.error(error, {
      context: error,
      tags: {
        componentName: "stores-mfe store-space",
        action: "getInitialProps"
      }
    });
  }

  return {
    slug,
    storeData,
    spaceData,
    meta: spaceMeta({
      storeName: storeData.name,
      spaceName: spaceData.name,
      storeSlug: slug,
      city: storeData.city,
      state: storeData.state
    }),
    is404,
    storeError
  };
};

StoreSpace.propTypes = {
  storeData: PropTypes.shape({
    spaces: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        about: PropTypes.string,
        aboutHtml: PropTypes.string,
        amenities: PropTypes.arrayOf(PropTypes.string),
        bookingEmail: PropTypes.string,
        displayOrder: PropTypes.number,
        hours: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string,
            openHour: PropTypes.string,
            closeHour: PropTypes.string
          })
        ),
        longDescription: PropTypes.string,
        name: PropTypes.string,
        shortDescription: PropTypes.string
      })
    ),
    classesUrl: PropTypes.string,
    eventsUrl: PropTypes.string,
    fullAddress: PropTypes.string,
    name: PropTypes.string,
    slug: PropTypes.string,
    websiteUrl: PropTypes.string
  }),
  spaceData: PropTypes.shape({
    id: PropTypes.string,
    about: PropTypes.string,
    aboutHtml: PropTypes.string,
    amenities: PropTypes.arrayOf(PropTypes.string),
    bookingEmail: PropTypes.string,
    displayOrder: PropTypes.number,
    hours: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        openHour: PropTypes.string,
        closeHour: PropTypes.string
      })
    ),
    longDescription: PropTypes.string,
    name: PropTypes.string,
    images: PropTypes.arrayOf(PropTypes.object),
    shortDescription: PropTypes.string
  }),
  meta: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    canonicalPath: PropTypes.string,
    robots: PropTypes.string
  })
};

StoreSpace.defaultProps = {
  storeData: {},
  spaceData: {},
  meta: {}
};

export default StoreSpace;
